import { Rectangle, Segment } from '@thive/fddm-types';
import { Vector3 } from 'three';

interface BoundingBoxExtent {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Identifiable {
  id: string;
}

export const getIds = <Item extends Identifiable>(items: readonly Item[]): string[] =>
  items.map((item) => item.id);

export const indexById = <Item extends Identifiable>(items: readonly Item[]): Map<string, Item> =>
  new Map(items.map((item) => [item.id, item]));

export const filterFirstMatching = <Item>(
  items: Item[],
  areEqual: (left: Item, right: Item) => boolean,
): Item[] =>
  items.filter(
    (item, index) => items.findIndex((otherItem) => areEqual(otherItem, item)) === index,
  );

export const areCoordinatesEqual = (
  leftX: number,
  leftY: number,
  rightX: number,
  rightY: number,
  tolerance: number,
): boolean => Math.abs(leftX - rightX) <= tolerance && Math.abs(leftY - rightY) <= tolerance;

export const createPaddedBoundingBoxRectangle = (
  boundingBox: BoundingBoxExtent,
  padding: number,
): Rectangle => {
  const isHorizontal = boundingBox.width > boundingBox.height;

  return {
    centerX: boundingBox.x + boundingBox.width / 2,
    centerY: boundingBox.y + boundingBox.height / 2,
    width: Math.min(boundingBox.width, boundingBox.height) + padding,
    length: Math.max(boundingBox.width, boundingBox.height) + padding,
    angle: isHorizontal ? 0 : Math.PI / 2,
  };
};

export const segmentsToPathPoints = (segments: Segment[]): Vector3[] => {
  const firstSegment = segments[0];
  if (!firstSegment) return [];

  return [
    new Vector3(firstSegment.start.x, firstSegment.start.y),
    ...segments.map((segment) => new Vector3(segment.end.x, segment.end.y)),
  ];
};

export const segmentToRectangle = (segment: Segment): Rectangle => {
  const deltaX = segment.end.x - segment.start.x;
  const deltaY = segment.end.y - segment.start.y;
  const angle = Math.atan2(deltaY, deltaX);

  const centerOffset = (segment.leftSideWidth - segment.rightSideWidth) / 2;
  const normalX = -Math.sin(angle);
  const normalY = Math.cos(angle);

  return {
    centerX: (segment.start.x + segment.end.x) / 2 + normalX * centerOffset,
    centerY: (segment.start.y + segment.end.y) / 2 + normalY * centerOffset,
    length: Math.hypot(deltaX, deltaY),
    width: segment.leftSideWidth + segment.rightSideWidth,
    angle,
  };
};
